import { cloneElement, ReactElement, ReactNode, useEffect } from "react";
import {
  Box,
  BoxProps,
  Button,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  HStack,
  Icon,
  IconButton,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { MenuAlt1Icon, KeyIcon, UserGroupIcon, ViewGridAddIcon, XIcon, ChatIcon } from "@heroicons/react/outline";
import { NavLink } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import Logo from "./Logo";

/**
 * Responsive sidebar with header
 * @param expand The breakpoint, below which, the Navbar will collapse.
 */
export default function SidebarWithHeader({ expand = "lg" }: { expand?: "md" | "lg" }) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, [expand]: false });

  useEffect(() => {
    if (!isMobile) onClose();
  }, [isMobile, onClose]);

  const { onLogout } = useAuth();

  return (
    <>
      <Box
        as="header"
        pos="fixed"
        zIndex="1"
        top="0"
        insetStart="0"
        w="full"
        height="var(--header-height, 80px)"
        bgColor="white"
        shadow="md"
      >
        <HStack spacing="10" justify="space-between" h="full" px="5">
          <Logo />
          <Button variant="outline" display={{ base: "none", [expand]: "inline-flex" }} onClick={onLogout}>
            تسجيل الخروج
          </Button>
          <IconButton
            variant="outline"
            icon={<Icon as={isOpen ? XIcon : MenuAlt1Icon} fontSize="1.15rem" />}
            aria-label="Open Menu"
            onClick={onOpen}
            display={{ base: "inline-flex", [expand]: "none" }}
          />
        </HStack>
      </Box>
      <SidebarContent
        as="aside"
        display={{ base: "none", [expand]: "block" }}
        top="var(--header-height, 80px)"
        width="var(--sidebar-width, 300px)"
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        returnFocusOnClose={false}
        closeOnOverlayClick
      >
        <DrawerOverlay />
        <DrawerContent>
          <SidebarContent
            onNavClick={onClose}
            extendStart={<Logo mb="6" />}
            extendEnd={
              <Box mt="auto">
                <Button w="full" mt="8" onClick={onLogout}>
                  تسجيل الخروج
                </Button>
              </Box>
            }
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}

const nav = [
  { to: "/categories", title: "التخصصات", icon: ViewGridAddIcon },
  { to: "/admins", title: "مشرفي الموقع", icon: UserGroupIcon },
  { to: "/employees", title: "الموظفين", icon: UserGroupIcon },
  { to: "/settings", title: "الإعدادات", icon: KeyIcon },
  { to: "/messages", title: "الرسائل", icon: ChatIcon },
];

function SidebarContent({
  extendStart,
  extendEnd,
  onNavClick,
  ...rest
}: BoxProps & { extendStart?: ReactNode; extendEnd?: ReactNode; onNavClick?: () => void }) {
  return (
    <Box
      pos="fixed"
      insetStart="0"
      top="0"
      width="full"
      height="full"
      px="4"
      pt="8"
      pb="5"
      display="flex"
      flexDirection="column"
      bgColor="white"
      overflowY="auto"
      {...rest}
    >
      {extendStart}
      <VStack>
        {nav.map(({ icon, ...rest }, i) => (
          <NavItem key={i} {...rest} icon={<Icon as={icon} fontSize="xl" />} onClick={onNavClick} />
        ))}
      </VStack>
      {extendEnd}
    </Box>
  );
}

interface NavItemProps {
  icon: ReactElement;
  title: string;
  to: string;
  onClick?: () => void;
}

function NavItem({ icon, title, ...rest }: NavItemProps) {
  return (
    <Button
      as={NavLink}
      variant="ghost"
      py="6"
      w="full"
      justifyContent="flex-start"
      leftIcon={icon}
      // @ts-ignore
      style={({ isActive }) => ({
        backgroundColor: isActive ? "var(--chakra-colors-gray-200)" : undefined,
      })}
      {...rest}
    >
      {title}
    </Button>
  );
}
